import { SettingsPermissions } from "@redotech/account-common/permissions";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import type { OverrideAdjustmentState } from "@redotech/redo-return-core-common/return-flow-step-valid";
import type { AdjustmentAmounts } from "@redotech/redo-return-core-common/return-flow/adjustment-unit";
import {
  StepId,
  StepTypeDetailsProps,
} from "@redotech/redo-return-ui/flows/step";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import { InfoTooltip } from "@redotech/redo-web/info-tooltip";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import { AdjustmentElement } from "./return";
import { isBonusAdjustment } from "./util";

export const OverrideAdjustmentDetails = memo(
  function OverrideAdjustmentDetails({
    stepSelect,
    state,
    setState,
    flowType,
  }: StepTypeDetailsProps<OverrideAdjustmentState>) {
    const canEditReturnsSettings = useHasPermission(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    );
    const { t } = useTranslation();
    const nextStepChange = useHandler((next: StepId | undefined) => {
      setState((state) => ({ ...state, next }));
    });
    const handleAdjustmentFlat = useHandler((adjustmentFlat: string) => {
      setState((state) => ({
        ...state,
        adjustmentFlat,
        isUseItOrLoseIt: isBonusAdjustment(adjustmentFlat)
          ? state.isUseItOrLoseIt
          : false, // If it's a fee, force this to be false
      }));
    });
    const handleAdjustmentPercent = useHandler((adjustmentPercent: string) => {
      setState((state) => ({
        ...state,
        adjustmentPercent,
        isUseItOrLoseIt: isBonusAdjustment(adjustmentPercent)
          ? state.isUseItOrLoseIt
          : false, // If it's a fee, force this to be false
      }));
    });
    const handleAmountsChange = useHandler((next: AdjustmentAmounts) => {
      setState((state) => ({
        ...state,
        adjustmentFlat: next.adjustmentFlat,
        adjustmentPercent: next.adjustmentPercent,
        perCurrencyAmountsEnabled: next.perCurrencyAmountsEnabled,
        isUseItOrLoseIt:
          isBonusAdjustment(next.adjustmentFlat) &&
          isBonusAdjustment(next.adjustmentPercent)
            ? state.isUseItOrLoseIt
            : false,
      }));
    });

    const handleIsUseItOrLoseIt = useHandler((isUseItOrLoseIt: boolean) => {
      setState((state) => ({ ...state, isUseItOrLoseIt }));
    });
    const handleCurrencyOverrides = useHandler(
      (currencyOverrides: Record<string, string>) => {
        setState((state) => ({ ...state, currencyOverrides }));
      },
    );
    const handlePerCurrencyAmountsEnabled = useHandler((enabled: boolean) => {
      setState((state) => ({ ...state, perCurrencyAmountsEnabled: enabled }));
    });

    const isBonus =
      isBonusAdjustment(state.adjustmentFlat || "0") &&
      isBonusAdjustment(state.adjustmentPercent || "0");

    return (
      <>
        <AdjustmentElement
          adjustmentFlat={state.adjustmentFlat}
          adjustmentPercent={state.adjustmentPercent}
          canEditReturnsSettings={canEditReturnsSettings}
          currencyOverrides={state.currencyOverrides}
          handleAdjustmentFlat={handleAdjustmentFlat}
          handleAdjustmentPercent={handleAdjustmentPercent}
          handleAmountsChange={handleAmountsChange}
          handleCurrencyOverridesChange={handleCurrencyOverrides}
          handlePerCurrencyAmountsEnabledChange={
            handlePerCurrencyAmountsEnabled
          }
          perCurrencyAmountsEnabled={state.perCurrencyAmountsEnabled}
        />
        {isBonus && (
          <Flex align="center" gap="xs">
            <RedoToggle
              disabled={!canEditReturnsSettings}
              label={t("Use it or lose it")}
              setValue={handleIsUseItOrLoseIt}
              size="xs"
              value={state.isUseItOrLoseIt ?? false}
            />
            <InfoTooltip
              boxSize={16}
              tooltip={{
                title: t(
                  "If enabled, any excess bonus not applied to the exchange will be lost rather than rolling over as store credit or refund.",
                ),
              }}
            />
          </Flex>
        )}
        <Text fontSize="xs" textColor="secondary">
          {t("Next step")}
        </Text>
        {stepSelect({ value: state.next, valueChange: nextStepChange })}
      </>
    );
  },
);
